<template>
  <div class="ei-strength-growth-profile">
    <h2
        v-if="showTitle"
        class="ei-strength-growth-profile__title"
    >
      {{ title }}
    </h2>

    <div class="ei-strength-growth-profile__grid">
      <a-card
          :bordered="true"
          class="ei-strength-growth-profile__card"
      >
        <template #title>
          <div class="ei-strength-growth-profile__card-title">
            <span>{{ averageCardTitle }}</span>
          </div>
        </template>

        <EiScaleRowsTable
            class="ei-strength-growth-profile__scale-table"
            :sections="averageProfileSections"
            mode="single"
            :show-difference="false"
            :show-track-values="false"
            :show-scale-after-each-section="true"
            :show-first-section-title="false"
            :default-color="defaultColor"
            :scale-height="8"
            :row-gap="12"
        />
      </a-card>

      <a-card
          :bordered="true"
          class="ei-strength-growth-profile__card"
      >
        <template #title>
          <div class="ei-strength-growth-profile__card-title">
            <span>{{ detailsCardTitle }}</span>
          </div>
        </template>

        <EiScaleRowsTable
            class="ei-strength-growth-profile__scale-table"
            :sections="detailsSections"
            mode="single"
            :show-difference="false"
            :show-track-values="false"
            :show-scale-after-each-section="false"
            :show-first-section-title="true"
            :default-color="defaultColor"
            :scale-height="8"
            :row-gap="12"
        />
      </a-card>
    </div>

    <div
        v-if="showFooter"
        class="ei-strength-growth-profile__footer"
        data-html2canvas-ignore
    >
      <a-button
          class="secondary-button"
          @click="scrollTop"
      >
        Наверх
      </a-button>
    </div>
  </div>
</template>

<script>
import { QuestionCircleOutlined } from '@ant-design/icons-vue'
import EiScaleRowsTable from '@/components/Analytics/EiScaleRowsTable.vue'

export default {
  name: 'EiStrengthGrowthProfileBlock',

  components: {
    EiScaleRowsTable,
    QuestionCircleOutlined,
  },

  props: {
    title: {
      type: String,
      default: 'ЭИ-профиль с сильными сторонами и зонами роста',
    },

    showTitle: {
      type: Boolean,
      default: true,
    },

    showFooter: {
      type: Boolean,
      default: false,
    },

    averageCardTitle: {
      type: String,
      default: 'Средний ЭИ-профиль',
    },

    detailsCardTitle: {
      type: String,
      default: 'Детализация ЭИ (10 разделов способностей)',
    },

    averageHelpText: {
      type: String,
      default: 'Средний профиль показывает общий ЭИ, домены и ветви эмоционального интеллекта.',
    },

    detailsHelpText: {
      type: String,
      default: 'Детализация показывает уровень по десяти разделам способностей эмоционального интеллекта.',
    },

    averageProfileSections: {
      type: Array,
      default: () => [],
    },

    detailsSections: {
      type: Array,
      default: () => [],
    },

    defaultColor: {
      type: String,
      default: '#D5DBE2',
    },
  },

  setup() {
    const scrollTop = () => {
      const container = document.querySelector('.page-container')

      if (container) {
        container.scrollTo({
          top: 0,
          behavior: 'smooth',
        })

        return
      }

      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      })
    }

    return {
      scrollTop,
    }
  },
}
</script>

<style scoped lang="scss">
.ei-strength-growth-profile {
  width: 100%;
  min-width: 0;
  max-width: 100%;
  text-align: start;
  font-family: "Open Sans", sans-serif;
}

.ei-strength-growth-profile__title {
  margin: 0 0 16px;
  font-weight: 600;
  font-size: 16px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.88);
}

.ei-strength-growth-profile__grid {
  width: 100%;
  min-width: 0;
  max-width: 100%;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  align-items: start;
}

.ei-strength-growth-profile__card {
  width: 100%;
  min-width: 0;
  max-width: 100%;
  border-radius: 8px;
}

.ei-strength-growth-profile__card :deep(.ant-card-head) {
  min-height: auto;
  border-bottom: none;
  padding: 0 20px !important;
}

.ei-strength-growth-profile__card :deep(.ant-card-head-title) {
  padding: 20px 0 0;
}

.ei-strength-growth-profile__card-title {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  min-width: 0;
  font-weight: 600;
  font-size: 16px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.85);
  font-family: "Open Sans", sans-serif;
}

.ei-strength-growth-profile__help-icon {
  flex: 0 0 auto;
  color: rgba(0, 0, 0, 0.25);
  font-size: 14px;
  cursor: pointer;
}

.ei-strength-growth-profile__popover {
  max-width: 260px;
  font-size: 14px;
  line-height: 120%;
}

.ei-strength-growth-profile__scale-table {
  width: 100%;
}

.ei-strength-growth-profile__scale-table :deep(.ei-scale-table__section-title) {
  margin-bottom: 8px;
  font-weight: 400;
  font-size: 14px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.45);
}

.ei-strength-growth-profile__scale-table :deep(.ei-scale-table__row-header) {
  margin-bottom: 8px;
  font-weight: 400;
  font-size: 14px;
  line-height: 120%;
}

.ei-strength-growth-profile__scale-table :deep(.ei-scale-table__label) {
  gap: 6px;
}

.ei-strength-growth-profile__scale-table :deep(.ei-scale-table__value) {
  min-width: 34px;
  font-weight: 600;
}

.ei-strength-growth-profile__scale-table :deep(.ei-scale-table__track) {
  background-color: #EEF1F5;
}

.ei-strength-growth-profile__scale-table :deep(.ei-scale-table__scale) {
  margin-top: 6px;
  margin-bottom: 18px;
  font-size: 11px;
}

.ei-strength-growth-profile__footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 28px;
}

@media (max-width: $tablet-breakpoint) {
  .ei-strength-growth-profile__grid {
    grid-template-columns: 1fr;
  }
}
</style>